import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { getDateLocale } from '../../i18n';
import { useRestaurants } from '../../hooks/useRestaurants';
import { io, Socket } from 'socket.io-client';
import { api, API_URL } from '../../utils/apiClient';
import { LiveStatsBar } from '../../components/LiveStatsBar';
import './AnalyticsPanel.css';

interface AnalyticsData {
  period: string;
  summary: {
    totalOrders: number;
    completedOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    upsellConversionRate: number;
    upsellRevenue: {
      gross: number;
      commission: number;
      net: number;
      count: number;
    };
    totalUpsells: number;
  };
  popularItems: Array<{
    name: string;
    count: number;
    revenue: number;
  }>;
  statusDistribution: {
    PENDING: number;
    PREPARING: number;
    READY: number;
    COMPLETED: number;
    CANCELLED: number;
  };
  dailyRevenue: Array<{
    date: string;
    revenue: number;
    orders: number;
  }>;
}

interface DishRating {
  productId: string;
  name: string;
  avgStars: number;
  reviewCount: number;
  distribution: Record<string, number>;
  recentCount: number;
  trendDelta: number | null;
}

// Filled/empty star glyphs for a 0–5 value (rounded to nearest whole star).
function renderStars(value: number): string {
  const full = Math.max(0, Math.min(5, Math.round(value)));
  return '★'.repeat(full) + '☆'.repeat(5 - full);
}

function AnalyticsPanel() {
  const { t } = useTranslation();
  const { restaurants, error: restaurantsError, fetchRestaurants } = useRestaurants();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('7d');
  const [ratings, setRatings] = useState<DishRating[]>([]);
  const [ratingsSummary, setRatingsSummary] = useState<{ totalReviews: number; overallAvg: number }>({
    totalReviews: 0,
    overallAvg: 0,
  });
  const [recentComments, setRecentComments] = useState<
    { productName: string; stars: number; comment: string; createdAt: string }[]
  >([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveRefreshRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    fetchRestaurants().then((filtered) => {
      if (filtered.length > 0 && !selectedRestaurantId) setSelectedRestaurantId(filtered[0].id);
    });
  }, [fetchRestaurants]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedRestaurantId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<AnalyticsData>(`/api/analytics/restaurant/${selectedRestaurantId}`, {
        params: { period },
      });
      setAnalytics(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'analytics.failedLoad');
    } finally {
      setLoading(false);
    }
  }, [selectedRestaurantId, period]);

  useEffect(() => {
    if (selectedRestaurantId) fetchAnalytics();
    else setAnalytics(null);
  }, [selectedRestaurantId, fetchAnalytics]);

  useEffect(() => {
    if (!selectedRestaurantId) {
      setRatings([]);
      setRatingsSummary({ totalReviews: 0, overallAvg: 0 });
      setRecentComments([]);
      return;
    }
    api
      .get<{
        summary: { totalReviews: number; overallAvg: number };
        ratings: DishRating[];
        recentComments: { productName: string; stars: number; comment: string; createdAt: string }[];
      }>(`/api/analytics/restaurant/${selectedRestaurantId}/product-ratings`)
      .then(({ data }) => {
        setRatings(data.ratings ?? []);
        setRatingsSummary(data.summary ?? { totalReviews: 0, overallAvg: 0 });
        setRecentComments(data.recentComments ?? []);
      })
      .catch(() => {});
  }, [selectedRestaurantId]);

  const formatCurrency = (amount: number) => `₸${Math.round(amount).toLocaleString('ru-RU')}`;

  useEffect(() => {
    if (!selectedRestaurantId) return;

    if (!socketRef.current) {
      socketRef.current = io(API_URL);
    }
    const socket = socketRef.current;

    socket.emit('join-restaurant', selectedRestaurantId);
    if (socket.connected) setSocketConnected(true);
    socket.on('connect', () => setSocketConnected(true));
    socket.on('disconnect', () => setSocketConnected(false));

    const handleUpdate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchAnalytics();
        liveRefreshRef.current?.();
      }, 1500);
    };

    socket.on('analytics-update', handleUpdate);

    return () => {
      socket.off('analytics-update', handleUpdate);
      socket.off('connect');
      socket.off('disconnect');
      socket.emit('leave-restaurant', selectedRestaurantId);
    };
  }, [selectedRestaurantId, fetchAnalytics]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' });
  };

  const getMaxRevenue = () => {
    if (!analytics?.dailyRevenue.length) return 1;
    return Math.max(...analytics.dailyRevenue.map(d => d.revenue), 1);
  };

  const handleExportCSV = () => {
    if (!analytics || !selectedRestaurantId) return;

    const restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    const restaurantName = restaurant?.name || 'Restaurant';
    
    // Create CSV content
    let csv = `Analytics Report - ${restaurantName}\n`;
    csv += `Period: ${period === '7d' ? '7 Days' : period === '30d' ? '30 Days' : period === '90d' ? '90 Days' : 'All Time'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    // Summary section
    csv += `SUMMARY\n`;
    csv += `Total Orders,${analytics.summary.totalOrders}\n`;
    csv += `Completed Orders,${analytics.summary.completedOrders}\n`;
    csv += `Total Revenue,${analytics.summary.totalRevenue.toFixed(2)}\n`;
    csv += `Average Order Value,${analytics.summary.averageOrderValue.toFixed(2)}\n`;
    csv += `Upsell Conversion Rate,${analytics.summary.upsellConversionRate.toFixed(2)}%\n`;
    csv += `Upsell Revenue (Gross),${analytics.summary.upsellRevenue.gross.toFixed(2)}\n`;
    csv += `Upsell Revenue (Commission),${analytics.summary.upsellRevenue.commission.toFixed(2)}\n`;
    csv += `Upsell Revenue (Net),${analytics.summary.upsellRevenue.net.toFixed(2)}\n`;
    csv += `Total Upsells,${analytics.summary.totalUpsells}\n\n`;
    
    // Daily Revenue section
    csv += `DAILY REVENUE (Last 7 Days)\n`;
    csv += `Date,Revenue,Orders\n`;
    analytics.dailyRevenue.forEach(day => {
      csv += `${day.date},${day.revenue.toFixed(2)},${day.orders}\n`;
    });
    csv += `\n`;
    
    // Popular Items section
    csv += `POPULAR ITEMS\n`;
    csv += `Rank,Item Name,Quantity Sold,Revenue\n`;
    analytics.popularItems.forEach((item, idx) => {
      csv += `${idx + 1},${item.name},${item.count},${item.revenue.toFixed(2)}\n`;
    });
    
    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `analytics-${restaurantName}-${period}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="analytics-panel">
      <div className="panel-card">
        <div className="analytics-header">
          <div className="control-group">
            <select
              value={selectedRestaurantId}
              onChange={(e) => setSelectedRestaurantId(e.target.value)}
              className="restaurant-select"
            >
              <option value="">{t('analytics.selectRestaurant')}</option>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <div className="period-selector">
              <button
                className={`period-btn ${period === '7d' ? 'active' : ''}`}
                onClick={() => setPeriod('7d')}
              >
                {t('analytics.period7d')}
              </button>
              <button
                className={`period-btn ${period === '30d' ? 'active' : ''}`}
                onClick={() => setPeriod('30d')}
              >
                {t('analytics.period30d')}
              </button>
              <button
                className={`period-btn ${period === '90d' ? 'active' : ''}`}
                onClick={() => setPeriod('90d')}
              >
                {t('analytics.period90d')}
              </button>
              <button
                className={`period-btn ${period === 'all' ? 'active' : ''}`}
                onClick={() => setPeriod('all')}
              >
                {t('analytics.periodAll')}
              </button>
            </div>
            <motion.button
              onClick={fetchAnalytics}
              className="btn-primary"
              disabled={!selectedRestaurantId}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('analytics.refresh')}
            </motion.button>
            {analytics && (
              <motion.button
                onClick={handleExportCSV}
                className="btn-secondary"
                disabled={!selectedRestaurantId}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('analytics.exportCsv')}
              </motion.button>
            )}
            {socketConnected && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: 'var(--a-green-pulse)', fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--a-green-pulse)', display: 'inline-block' }} />
                LIVE
              </span>
            )}
          </div>
        </div>

        {(error || restaurantsError) && (
          <div className="error-box">
            {(error || restaurantsError).startsWith('analytics.') ? t(error || restaurantsError) : (error || restaurantsError)}
          </div>
        )}

        {selectedRestaurantId && (
          <LiveStatsBar
            restaurantId={selectedRestaurantId}
            onRegisterRefresh={(cb) => { liveRefreshRef.current = cb; }}
          />
        )}

        {loading ? (
          <div className="loading-state">{t('analytics.loading')}</div>
        ) : !selectedRestaurantId ? (
          <div className="empty-state">
            {t('analytics.selectRestaurantPrompt')}
          </div>
        ) : analytics ? (
          <>
            {/* Summary Cards */}
            <div className="analytics-summary">
              <div className="summary-card">
                <div className="summary-icon"></div>
                <div className="summary-content">
                  <div className="summary-label">{t('analytics.totalOrders')}</div>
                  <div className="summary-value">{analytics.summary.totalOrders}</div>
                  <div className="summary-sublabel">{analytics.summary.completedOrders} {t('analytics.completed')}</div>
                </div>
              </div>

              <div className="summary-card revenue-card">
                <div className="summary-icon"></div>
                <div className="summary-content">
                  <div className="summary-label">{t('analytics.totalRevenue')}</div>
                  <div className="summary-value">{formatCurrency(analytics.summary.totalRevenue)}</div>
                  <div className="summary-sublabel">{t('analytics.avg')}: {formatCurrency(analytics.summary.averageOrderValue)}</div>
                </div>
              </div>

              <div className="summary-card upsell-card">
                <div className="summary-icon"></div>
                <div className="summary-content">
                  <div className="summary-label">{t('analytics.upsellRevenue')}</div>
                  <div className="summary-value">{formatCurrency(analytics.summary.upsellRevenue.gross)}</div>
                  <div className="summary-sublabel">
                    {analytics.summary.upsellConversionRate.toFixed(1)}% {t('analytics.conversion')}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Комиссия OLSO{analytics.summary.upsellRevenue.gross > 0 ? ` (${(analytics.summary.upsellRevenue.commission / analytics.summary.upsellRevenue.gross * 100).toFixed(1)}%)` : ''}: {formatCurrency(analytics.summary.upsellRevenue.commission)}
                    <br />
                    Ваш доход: <strong>{formatCurrency(analytics.summary.upsellRevenue.net)}</strong>
                    {' '}({analytics.summary.upsellRevenue.count} допродаж)
                  </div>
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-icon"></div>
                <div className="summary-content">
                  <div className="summary-label">{t('analytics.upsells')}</div>
                  <div className="summary-value">{analytics.summary.totalUpsells}</div>
                  <div className="summary-sublabel">{t('analytics.itemsSold')}</div>
                </div>
              </div>
            </div>

            {/* Daily Revenue Chart */}
            {analytics.dailyRevenue.length > 0 && (
              <div className="analytics-section">
                <h3 className="section-title">{t('analytics.dailyRevenue')}</h3>
                <div className="chart-container">
                  <div className="chart-bars">
                    {analytics.dailyRevenue.map((day, idx) => {
                      const maxRevenue = getMaxRevenue();
                      const height = (day.revenue / maxRevenue) * 100;
                      return (
                        <div key={idx} className="chart-bar-wrapper">
                          <div className="chart-bar" style={{ height: `${height}%` }}>
                            <div className="chart-bar-value">{formatCurrency(day.revenue)}</div>
                          </div>
                          <div className="chart-bar-label">{formatDate(day.date)}</div>
                          <div className="chart-bar-sublabel">{day.orders} {t('analytics.orders')}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Popular Items */}
            {analytics.popularItems.length > 0 && (
              <div className="analytics-section">
                <h3 className="section-title">{t('analytics.popularItems')}</h3>
                <div className="popular-items-list">
                  {analytics.popularItems.map((item, idx) => (
                    <div key={idx} className="popular-item">
                      <div className="popular-item-rank">#{idx + 1}</div>
                      <div className="popular-item-info">
                        <div className="popular-item-name">{item.name}</div>
                        <div className="popular-item-stats">
                          <span>{item.count} {t('analytics.sold')}</span>
                          <span>•</span>
                          <span>{formatCurrency(item.revenue)}</span>
                        </div>
                      </div>
                      <div className="popular-item-bar">
                        <div
                          className="popular-item-bar-fill"
                          style={{
                            width: `${(item.count / analytics.popularItems[0].count) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dish Ratings */}
            <div className="analytics-section">
              <h3 className="section-title">{t('analytics.dishRatings', 'Оценки блюд')}</h3>
              {ratings.length === 0 ? (
                <p className="analytics-empty">{t('analytics.noRatings', 'Пока нет оценок')}</p>
              ) : (
                <>
                  <div className="dr-summary">
                    <div className="dr-summary-score">
                      <span className="dr-summary-num">{ratingsSummary.overallAvg.toFixed(1)}</span>
                      <span className="dr-stars dr-stars--lg" aria-hidden>{renderStars(ratingsSummary.overallAvg)}</span>
                    </div>
                    <div className="dr-summary-meta">
                      {t('analytics.avgRating', 'Средняя оценка')} · {ratingsSummary.totalReviews} {t('analytics.reviewsCount', 'оценок')}
                    </div>
                  </div>

                  <div className="dr-list">
                    {ratings.map((r) => {
                      const counts = [1, 2, 3, 4, 5].map((s) => r.distribution[String(s)] ?? 0);
                      const maxCount = Math.max(1, ...counts);
                      return (
                        <div key={r.productId} className="dr-card">
                          <div className="dr-card-head">
                            <span className="dr-name">{r.name}</span>
                            <span className="dr-avg">
                              <span className="dr-avg-num">{r.avgStars.toFixed(1)}</span>
                              <span className="dr-stars" aria-hidden>{renderStars(r.avgStars)}</span>
                            </span>
                          </div>
                          <div className="dr-meta">
                            <span className="dr-count">{r.reviewCount} {t('analytics.reviewsCount', 'оценок')}</span>
                            {r.trendDelta != null && r.trendDelta !== 0 ? (
                              <span className={`dr-trend ${r.trendDelta > 0 ? 'up' : 'down'}`}>
                                {r.trendDelta > 0 ? '▲' : '▼'} {Math.abs(r.trendDelta).toFixed(1)}
                                <span className="dr-trend-label"> {t('analytics.trend30d', 'за 30 дней')}</span>
                              </span>
                            ) : (
                              <span className="dr-trend dr-trend--flat">{t('analytics.trendFlat', '— стабильно')}</span>
                            )}
                          </div>
                          <div className="dr-dist">
                            {[5, 4, 3, 2, 1].map((s) => {
                              const c = r.distribution[String(s)] ?? 0;
                              return (
                                <div key={s} className="dr-dist-row">
                                  <span className="dr-dist-star">{s}★</span>
                                  <span className="dr-dist-bar">
                                    <span className="dr-dist-fill" style={{ width: `${(c / maxCount) * 100}%` }} />
                                  </span>
                                  <span className="dr-dist-count">{c}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {recentComments.length > 0 && (
                    <div className="dr-comments">
                      <h4 className="section-title">{t('analytics.recentComments', 'Последние комментарии')}</h4>
                      {recentComments.map((c, i) => (
                        <div key={i} className="dr-comment">
                          <div className="dr-comment-head">
                            <span className="dr-comment-name">{c.productName}</span>
                            <span className="dr-comment-stars" aria-hidden>{renderStars(c.stars)}</span>
                          </div>
                          <div className="dr-comment-text">{c.comment}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

          </>
        ) : (
          <div className="empty-state">{t('analytics.noData')}</div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPanel;

